import { Component } from "@kluntje/core";
import SSOService, { UserObject } from "Services/SSOService";
import {
  onEvent,
  removeEvent,
  removeClass,
  toggleClass,
  addClass,
  hasClass,
  getParent,
  findAll,
} from "@kluntje/js-utils/lib/dom-helpers";
import { getCurrentMQ } from "Helper/domHelper";
import { ACTIVE, HIDDEN, SCROLL_LOCK, SELECTED } from "Helper/cssClasses";
import { MQ } from "Services/MediaQueryService";
import { EventHandler } from "@kluntje/js-utils/lib/dom-helpers/lib/onEvent";

class FRAMainNavigation extends Component {
  SSOService = SSOService.getInstance();
  loggedIn?: boolean;
  toggleIcon!: HTMLElement;
  mainEl!: HTMLElement;

  constructor() {
    super({
      ui: {
        navigation: ".fra-m-main-navigation__wrapper:-one",
        primaryMenuItem: ".fra-m-main-navigation__item",
        primaryMenuLink: ".fra-m-main-navigation__item-link",
        rootMenu: ".fra-m-main-navigation__group:-one",
        backButton: ".fra-m-main-navigation__1-level-back",
        triggerActions: ".fra-m-main-navigation__actions-link[data-trigger]",
      },
      events: [
        {
          event: "click",
          target: "primaryMenuLink",
          handler: "toggleSubNavigation",
        },
        {
          event: "click",
          target: "backButton",
          handler: "toggleSubNavigation",
        },
        {
          event: "mouseover",
          target: "primaryMenuLink",
          handler: "hideActiveSubNavigation",
        },
        {
          event: "click",
          target: "triggerActions",
          handler: "onTriggerActionClick",
        },
        {
          event: "fra-toggled-layer-open",
          target: "window",
          handler: "onToggledLayerOpen",
        },
        {
          event: "fra-mq-change",
          target: "window",
          handler: "onMQChange",
        },
        {
          event: "fra-resize",
          target: "window",
          handler: "onResize",
        },
      ],
    });

    this.onAuthChange = this.onAuthChange.bind(this);
  }

  /**
   * Lifecycle-Hook: Initializes Component after render
   * @override
   */
  afterComponentRender(): void {
    this.toggleIcon = document.querySelector("#fra-e-toggle-nav .fra-e-icon") as HTMLElement;
    this.mainEl = document.querySelector(".fra-m-page-main") as HTMLElement;
    // bind toggle button event
    const toggleBtn = document.getElementById("fra-e-toggle-nav") as HTMLAnchorElement;
    onEvent(toggleBtn, "click", this.toggleNavigation as EventHandler<any>, this);
    this.highlightActiveMenuItem();

    this.SSOService.subscribe(this.onAuthChange);
    this.ui.primaryMenuLink.forEach((link: HTMLElement) => {
      link.setAttribute("tabindex", "0");
    })
  }

  onAuthChange({ loggedIn }: { loggedIn: boolean; user: UserObject | null }) {
    this.loggedIn = loggedIn;

    toggleClass(this, "fra-state-logged-in", loggedIn);
    toggleClass(this, "fra-state-logged-out", !loggedIn);
  }

  /**
   * Highlights active menu item by comparing it with the current site url
   */
  highlightActiveMenuItem(): void {
    const menuItems = [...this.ui.primaryMenuItem].filter(item => !hasClass(item, "fra-h-hide-mq4"));
    menuItems.forEach(item => {
      const itemLinks = findAll(
        item,
        ".fra-m-main-navigation__1-level-link:not([fra-m-main-navigation__1-level-back])",
      );
      // matching only works with internal links
      const firstItemLink = [...itemLinks].find(
        link => (link as HTMLAnchorElement).getAttribute("target") !== "_blank",
      );
      if (firstItemLink) {
        const itemLinkPath = (firstItemLink as HTMLAnchorElement).href.match(/\/[a-zA-Z]{2}\/([^/]+)/);
        const urlPath = window.location.href.match(/\/[a-zA-Z]{2}\/([^/]+)/);
        if (itemLinkPath && urlPath && itemLinkPath[1] === urlPath[1]) {
          addClass(item, SELECTED);
        }
      }
    });
  }

  /**
   * Open/close navigation in MQ1-3
   * @param {Event} e
   */
  toggleNavigation(e?: MouseEvent): void {
    if (e) {
      e.preventDefault();
    }

    // check whether menu is already active
    const navigationActive = !hasClass(this.ui.navigation, ACTIVE);

    if (navigationActive) {
      addClass(document.body, SCROLL_LOCK);
      this.activateTree();
    } else {
      removeClass(document.body, SCROLL_LOCK);
      this.deactivateTree();
    }
  }

  /**
   * Open/close sub menu in MQ 1-3 only on click
   * Show sub menu in MQ4-5 on hover (or click if touch device)
   * @param {Event} e
   */
  toggleSubNavigation(e: MouseEvent): void {
    if (!e || !e.currentTarget) {
      return;
    }
    // get link associated with click
    const eTarget = e.currentTarget as HTMLElement;
    const currentItem = eTarget.closest(".fra-m-main-navigation__item") as HTMLElement;
    if (!currentItem) {
      this.deactivateTree();
      return;
    }
    // if this is a direct link we don't need to take care of a menu
    if (hasClass(eTarget, "fra-m-main-navigation__item-link--direct")) {
      return;
    }

    // check whether sub menu is already active and bind click outside
    if (hasClass(currentItem, ACTIVE)) {
      // remove touch/click event from main (outside)
      removeEvent(this.mainEl, "click", this.deactivateTree, this);
    } else {
      e.preventDefault();
      // bind touch/click event to main (outside)
      onEvent(this.mainEl, "click", this.deactivateTree, this);
    }

    toggleClass(this.ui.rootMenu, ACTIVE);
    this.ui.navigation.scrollTop = 0;

    this.ui.primaryMenuItem.forEach((item: HTMLElement) => {
      // activate/deactivate item
      if (item === currentItem) {
        toggleClass(item, ACTIVE);
      } else {
        // deactivate other first levels in larger MQ
        removeClass(item, ACTIVE);
      }
    });
  }

  /**
   * hide other (click-)activated menu-items
   */
  hideActiveSubNavigation(): void {
    if (getCurrentMQ() === "MQ4" || getCurrentMQ() === "MQ5") {
      removeClass(this.ui.primaryMenuItem, ACTIVE);
    }
  }

  /**
   * Recalculate height of menu in MQ 3 and lower
   */
  setMenuHeight(): void {
    if (getCurrentMQ() !== "MQ4" && getCurrentMQ() !== "MQ5") {
      const header = document.querySelector(".fra-m-page-header") as HTMLElement;
      const nav = document.getElementById("fra-m-main-navigation") as HTMLElement;
      const minHeight = window.innerHeight - nav.offsetTop - header.offsetTop;
      this.ui.navigation.setAttribute("style", `height: ${minHeight}px`);
    } else {
      this.ui.navigation.setAttribute("style", "");
    }
  }
  /**
   * Show menu of first level items
   */
  activateTree(): void {
    // Close Global Search or Region/Language Picker layers.
    window.dispatchEvent(new CustomEvent("fra-close-header-layers"));
    // bind touch/click event to main (outside)
    onEvent(this.mainEl, "click", this.deactivateTree, this);
    // toggle icon in menu opener to close
    this.toggleIcon.setAttribute("icon-id", "fra-icon-close");
    // show menu
    toggleClass(this.ui.navigation, ACTIVE);
    // set height of menu
    this.setMenuHeight();
  }

  /**
   * Removes ACTIVE class from first level menu items
   */
  deactivateTree(): void {
    removeClass(this.ui.navigation, ACTIVE);
    // toggle icon in menu opener to default
    this.toggleIcon.setAttribute("icon-id", "fra-icon-menu");
    // remove touch/click event from main (outside)
    removeEvent(this.mainEl, "click", this.deactivateTree, this);

    removeClass(this.ui.rootMenu, ACTIVE);
    // remove active state
    removeClass(this.ui.primaryMenuItem, ACTIVE);
    // show any deactivated items
    removeClass(this.ui.primaryMenuItem, HIDDEN);
  }

  /**
   * EVENT HANDLERS
   */
  /**
   * Closes the menus if a header layer (Global Search or Region/Language Selector) has been opened.
   */
  onToggledLayerOpen(): void {
    this.deactivateTree();
  }

  /**
   * Triggers a click event on another link item. The connection is stated using a query selector in a data-trigger
   * attribute
   * @param {Event} e - the click event
   */
  onTriggerActionClick(e: Event): void {
    e.preventDefault();

    const actionLink = getParent(e.target as HTMLElement, ".fra-m-main-navigation__actions-link") as HTMLElement;
    const targetItemLink = this.querySelector(actionLink.dataset.trigger as string);

    if (targetItemLink !== null) {
      targetItemLink.dispatchEvent(new Event("click"));
    }
  }

  /**
   * Handle MQ change while menu is active
   */
  onMQChange({ detail: { oldMQ, newMQ } }: { detail: { oldMQ: MQ; newMQ: MQ } }): void {
    // check whether new MQ >= 4
    if ((newMQ === "MQ4" || newMQ === "MQ5") && (oldMQ !== "MQ4" || newMQ !== "MQ5")) {
      // new viewport size requires no menu
      removeClass(document.body, SCROLL_LOCK);
      // deactivate open menu and display first level links
      this.deactivateTree();
    }
    this.setMenuHeight();
  }
  /**
   * Handle resize event
   */
  onResize() {
    this.setMenuHeight();
  }
}

customElements.define("fra-m-main-navigation", FRAMainNavigation);
